import type { LensCardData } from '~/types/leLens';

import type { Row } from './liveModel';

export const clickFields = ['bucket', 'split', 'total_clicks', 'unique_clicks', 'items', 'ttv', 'gm'];

/** Decode only complete source groups, before existing count-mode and CSV reductions. */
export function completeClickCard(card: LensCardData): LensCardData {
  if (card.cardId !== 'clicks_rows') return card;
  if (card.truncated) return { ...card, rows: [] };
  const batches = card.rows || [];
  if (!batches.length || batches.every((row) => !('payload' in row))) return card;
  try {
    let total: number | undefined;
    const ids = new Set<number>();
    const rows = batches.flatMap((batch) => {
      const id = Number(batch.batch),
        count = Number(batch.record_count),
        expected = Number(batch.source_count);
      if (
        !Number.isSafeInteger(id) ||
        id < 0 ||
        ids.has(id) ||
        !Number.isSafeInteger(count) ||
        count < 1 ||
        !Number.isSafeInteger(expected) ||
        expected < count ||
        (total !== undefined && total !== expected)
      )
        throw Error('Invalid click evidence batch.');
      ids.add(id);
      total = expected;
      if (typeof batch.payload !== 'string') throw Error('Invalid click evidence payload.');
      const records: unknown = JSON.parse(batch.payload);
      if (!Array.isArray(records) || records.length !== count) throw Error('Invalid click evidence length.');
      return records.map((record) => {
        if (!Array.isArray(record) || record.length !== clickFields.length)
          throw Error('Invalid click evidence record.');
        return Object.fromEntries(clickFields.map((key, i) => [key, record[i]]));
      });
    });
    if (rows.length !== total || [...ids].some((id) => id >= ids.size))
      throw Error('Incomplete click evidence; figures and CSV are withheld.');
    return { ...card, rows };
  } catch (error) {
    return { ...card, status: 'error', rows: [], message: (error as Error).message };
  }
}

export function clickMarkIndex(rows: Array<Row>) {
  const marks = new Map<string, Map<string, Row>>();
  for (const row of rows) {
    const bucket = String(row.bucket),
      split = String(row.split ?? 'All');
    let values = marks.get(bucket);
    if (!values) {
      values = new Map();
      marks.set(bucket, values);
    }
    if (!values.has(split)) values.set(split, row);
  }
  return (bucket: string, split: string) => marks.get(bucket)?.get(split);
}

/** Bound visible rows only; full filtered evidence is passed separately to CSV. */
export function commsPage<T>(rows: Array<T>, page: number) {
  return rows.slice(page * 50, page * 50 + 50);
}
